import React from 'react';
import { Field, reduxForm } from 'redux-form';
import { Alert, Form, Button, Row, Col, FormGroup, Table, Panel, Label, Badge } from 'react-bootstrap';
import { FormActions, Static, FormFileUpload } from '../../common/components/formControls';
import { withTranslation } from 'react-i18next';
import ui from '../../common/services/ui';
import utils from '../../common/services/utils';
import { mapStatusToLabel } from '../paymentHistory/utils';
import { ConfirmationButton } from '../../common/components/formControls';
import BackButton from '../../common/components/BackButton';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LabelWithTooltip from '../../common/components/LabelWithTooltip';
import { faArrows, faCheck, faDollarSign, faTimes, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';
import InstalmentList from '../paymentHistory/InstalmentList';
import moment from 'moment';

const SettingsForm = ({ payment, error, handleSubmit, pristine, submitting, isReadonly, submitSucceeded, initialValues,
	canMarkAsPaid, canCancel, markAsPaid, markAsCancelled, canUploadInvoice,
	canSendInvoice, sendInvoice, canApplyPromoCode, onApplyPromoCode, canCancelSession, cancelSession, cancelAndRefundSession,
	cancelAllSessions, cancelAndRefundAllSessions, restoreCancelledSession, rescheduleSession, canRetryInstalment, retryInstalment,
	markInstalmentAsPaid, canMarkInstalmentAsPaid, onRefresh, t }) => {

	const documentData = initialValues && initialValues.invoice && {
		filename: initialValues.invoice.friendlyFilename,
		url: initialValues.invoice.url
	};

	const invoiceUrl = initialValues && initialValues.invoice && initialValues.invoice.url;

	const isInactive = initialValues && initialValues.isInactive;
	const parentId = initialValues && initialValues.parent.id;
	const childId = initialValues && initialValues.child.id;

	const campaignTags = initialValues && initialValues.campaignTags;
	const renderCampaignTags = campaignTags && Object.keys(campaignTags).map(key => <div key={key}>{key}: <b style={{ color: '#707070' }}>{campaignTags[key]}</b></div>);

	const paymentMethodName = (initialValues && initialValues.paymentMethod && initialValues.paymentMethod.name) || '';

	let allowRefund = initialValues && initialValues.remainingTotal > 0 && initialValues.status.id === 2;
	const isVoucherPayment = paymentMethodName === 'Voucher';
	const isGoCardlessPayment = paymentMethodName === 'GoCardless';
	const allowReschedule = initialValues && (initialValues.status.id === 2 || initialValues.status.id === 6);
	const paymentOption = initialValues && initialValues.paymentOption && `(${initialValues.paymentOption})`;

	const sessions = payment && payment.childSessions;
	const hasNoCanceledSessions = sessions && sessions.filter(l => !l.canceled).length > 0;
	let sessionRows = [];

	if (sessions && sessions.length > 0) {
		sessionRows = sessions.sort((a, b) => a.date > b.date ? 1 : -1).map((session, index) =>
			<tr key={index}>
				<td>{ui.formatDate(session.date)}</td>
				<td>{ui.formatTime(utils.parseTime((session.startTime || initialValues.startTime)))} - {ui.formatTime(utils.parseTime(session.endTime || initialValues.endTime))}</td>
				<td>{session.weekName}</td>
				<td><Badge bsStyle='success'>{session.isAttended ? t('Enrolment_Attended') : ''}</Badge></td>
				<td><Badge bsStyle='warning'>{session.canceled ? t('Enrolment_Canceled') : ''}</Badge></td>
				{canCancelSession && <td>
					{session.canceled && <ConfirmationButton bsStyle='warning' displayType='inline-block' style={{ marginBottom: '5px', marginRight: '5px' }}
						onConfirmation={() => restoreCancelledSession(session.id)}
						confirmationTitle={t('WorkshopPayment_RestoreCancelledSessionConfirmationTitle')}
						confirmationMessage={t('WorkshopPayment_RestoreCancelledSessionConfirmationMessage', { session_date: ui.formatDate(session.date) })}
						label={t('WorkshopPayment_RestoreCancelledSession')}
					/>}
					{!session.canceled && <ConfirmationButton bsStyle='warning' displayType='inline-block' style={{ marginBottom: '5px', marginRight: '5px' }}
						onConfirmation={() => cancelSession(session.id)}
						confirmationTitle={t('WorkshopPayment_CancelSessionConfirmationTitle')}
						confirmationMessage={t('WorkshopPayment_CancelSessionConfirmationMessage', { session_date: ui.formatDate(session.date) })}
						label={t('WorkshopPayment_CancelSession')}
					/>}
					{allowRefund && <Button bsStyle='danger' style={{ marginRight: '5px' }}
						onClick={() => cancelAndRefundSession(session.id, session.date)}><FontAwesomeIcon icon={faDollarSign} /> {t('PaymentHistory_CancelAndRefundSession')}</Button>}
					{allowReschedule && !session.canceled && session.isInTheFuture && <Button bsStyle='success'
						onClick={() => rescheduleSession(session.id, session.date, session.startTime, session.endTime, session.type, session.workshopSessionId)}><FontAwesomeIcon icon={faArrows} /> {t('PaymentHistory_RescheduleSession')}</Button>}

				</td>}
			</tr>
		);
	} else if (payment && payment.workshopSessions && (payment.status.id == 0 || payment.status.id == 1)) {
		sessionRows = payment.workshopSessions.map((session, index) => <tr key={index}>
			<td>{ui.formatDate(session.date)}</td>
			<td>{ui.formatTime(utils.parseTime((session.startTime || initialValues.startTime)))} - {ui.formatTime(utils.parseTime(session.endTime || initialValues.endTime))}</td>
			<td>{session.weekName}</td>
			{session.refunded && <td> <Label bsStyle="danger" style={{ marginLeft: 20 }}>{`${t('WorkshopSession_Refunded')}`}</Label> </td>}
		</tr>);
	}

	return (
		<Form horizontal onSubmit={handleSubmit}>
			{error && <Alert bsStyle='danger'>{error}</Alert>}
			{submitSucceeded && pristine && <Alert bsStyle='success'>{t('Common_UpdateSuccess')}</Alert>}
			{initialValues.isPaymentError && !isInactive && <Alert bsStyle='danger'>{initialValues.cancelationReason}</Alert>}
			<Row>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Order')}</Panel.Heading>
						<Panel.Body>
							<Field name='id' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Id')} />
							<Field name='created' component={Static} labelSize={4} horizontal formatType='dateTime' label={t('PaymentHistory_CreatedDate')} />
							<Field name='status' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Status')} valueWrapper={value => <span>
								<Label bsStyle={mapStatusToLabel(value.id)}>{t(`PaymentStatusType_${value.name}`)}</Label>
								{isInactive && <span> <Label bsStyle='inverse'>{t('PaymentStatus_Inactive')}</Label></span>}
							</span>} />
							<Field name='paymentDate' component={Static} labelSize={4} formatType='dateTime' horizontal label={t('PaymentHistory_PaymentDate')} />
						</Panel.Body>
					</Panel>
				</Col>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Parent')}</Panel.Heading>
						<Panel.Body>
							<Field name='parent.fullname' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Parent')} valueWrapper={value => <Link to={`/parents/${parentId}`}>{value}</Link>} />
							<Field name='parent.email' component={Static} labelSize={4} horizontal label={t('Parent_Email')} />
							<Field name='parent.phoneNumber' component={Static} labelSize={4} horizontal label={t('PaymentHistory_PhoneNumber')} />
							<Field name='child.fullname' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Child')} valueWrapper={value => <Link to={`/parents/${parentId}/children/${childId}`}>{value}</Link>} />
						</Panel.Body>
					</Panel>
				</Col>
			</Row>
			<Row>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Enrolment')}</Panel.Heading>
						<Panel.Body>
							<Field name='center' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Center')} />
							<Field name='workshop' component={Static} labelSize={4} horizontal label={t('WorkshopPayment_Workshop')} />
						</Panel.Body>
					</Panel>
				</Col>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_PaymentMethod')}</Panel.Heading>
						<Panel.Body>
							<Field name='paymentMethod.name' component={Static} labelSize={4} horizontal label={t('PaymentHistory_PaymentMethod')} valueWrapper={value => `${value} ${paymentOption || ''}`} />
							{initialValues.paymentCode && <Field name='paymentCode' component={Static} labelSize={4} horizontal label={t('PaymentHistory_PaymnetCode')} />}
							<Field name='transaction' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Transaction')} />
							{isVoucherPayment && <Field name='voucherSchemeName' component={Static} labelSize={4} horizontal label={t('PaymentHistory_VoucherSchemeName')} />}
							{isVoucherPayment && <Field name='voucherSchemeReference' component={Static} labelSize={4} horizontal label={t('PaymentHistory_VoucherSchemeReference')} />}
							{isGoCardlessPayment && <Field name='authorizationToken' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Mandate')} />}
							{payment.isInstalmentPayment && payment.subscriptionTransaction && <Field name='subscriptionTransaction' component={Static} labelSize={4} horizontal label={t(`PaymentHistory_${paymentMethodName}_Subscription`)} />}
						</Panel.Body>
					</Panel>
				</Col>
			</Row>
			<Row>
				<Col sm={12}>
					<Panel>
						<Panel.Heading>{t('WorkshopPayment_Sessions')}</Panel.Heading>
						<Panel.Body>
							<Table responsive>
								<tbody>
									{sessionRows}
								</tbody>
							</Table>
						</Panel.Body>
					</Panel>
				</Col>
			</Row>
			<Row>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Amounts')}</Panel.Heading>
						<Panel.Body>
							<Table>
								<thead>
									<tr>
										<th>{t('PaymentHistory_NumberOfSessions')}</th>
										<th>{t('PaymentHistory_Price')}</th>
										<th>{t('PaymentHistory_Subtotal')}</th>
									</tr>
								</thead>
								<tbody>
									{payment.paymentItems.map((item, index) =>
										<tr key={index}>
											<td>{item.quantity} x {item.description}</td>
											<td>{item.unitPrice}</td>
											<td>{ui.formatAmount(item.subtotal)}</td>
										</tr>)}
								</tbody>
								<tfoot>
									<tr><th colSpan={2}>{t('PaymentHistory_PaymentMethodFee')}</th><td>{ui.formatAmount(payment.paymentMethodFee)}</td></tr>
									<tr><th colSpan={2}><div>{t('PaymentHistory_Discount')}</div><div style={{ fontWeight: 'normal', color: '#000' }}>{payment.discountText}</div></th><td>{ui.formatAmount(payment.discount)}</td></tr>
									<tr><th colSpan={2}>{t('PaymentHistory_Total')}</th><td>{ui.formatAmount(payment.total)}</td></tr>
									{payment.refundedTotal && Number(payment.refundedTotal) > 0 ? <tr>
										<th>{t('PaymentHistory_TotalRefunded')}</th>
										<td>{Number(payment.refundedQuantity) > 0 ? payment.refundedQuantity : ''}</td>
										<td>{ui.formatAmount(payment.refundedTotal)}</td>
									</tr> : null}
									{payment.isInstalmentPayment ? <tr>
										<th colSpan={2}><LabelWithTooltip id='paidTotalTooltip' tooltip={t('PaymentHistory_PaidTotalTooltip')}>{t('PaymentHistory_PaidTotal')} <FontAwesomeIcon icon={faCircleInfo} className='text-muted' style={{ fontSize: '14px', marginLeft: '4px' }} /></LabelWithTooltip></th>
										<td>{ui.formatAmount(payment.paidTotal)}</td>
									</tr> : null}
									{payment.isInstalmentPayment ? <tr>
										<th colSpan={2}>{t('PaymentHistory_OutstandingTotal')}</th>
										<td>{ui.formatAmount(payment.outstandingTotal)}</td>
									</tr> : null}
								</tfoot>
							</Table>
						</Panel.Body>
					</Panel>
				</Col>
				<Col sm={6}>
					<Panel>
						<Panel.Heading>{t('PaymentHistory_Notes')}</Panel.Heading>
						<Panel.Body>
							{campaignTags && <Field name='campaignTags' component={Static} labelSize={4} horizontal label={t('PaymentHistory_Campaign')} valueWrapper={value => <span>{renderCampaignTags}</span>} />}
							<Field name='notes' component={Static} labelSize={0} formatType='html' horizontal />
						</Panel.Body>
					</Panel>
				</Col>
			</Row>
			{payment.isInstalmentPayment && <InstalmentList instalments={payment.instalments.sort((a, b) => moment(a.chargeDate) - moment(b.chargeDate))} instalmentTemplateName={payment.instalmentTemplateName} instalmentPlan={payment.instalmentPlan}
				canRetryInstalment={canRetryInstalment} retryInstalment={retryInstalment} paymentMethod={payment.paymentMethod} onRefresh={onRefresh} markInstalmentAsPaid={markInstalmentAsPaid} canMarkInstalmentAsPaid={canMarkInstalmentAsPaid} />}
			{canUploadInvoice && <Field name='invoiceId' component={FormFileUpload} disabled={isReadonly} horizontal label={t('PaymentHistory_Invoice')} initialData={documentData} />}
			{!canUploadInvoice && invoiceUrl && <FormGroup>
				<Col sm={10} smOffset={2}>
					<a target='blank' href={invoiceUrl}>{t('PaymentHistory_ViewInvoice')}</a>
				</Col>
			</FormGroup>}

			<FormActions horizontal>
				<BackButton />
				<Button type='submit' disabled={isReadonly || pristine || submitting} bsStyle='primary'>{t('Common_Save')}</Button>
				<div className='pull-right'>
					{canMarkAsPaid && <Button onClick={markAsPaid} bsStyle='primary' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faCheck} /> {t('PaymentHistory_MarkAsPaid')}</Button>}
					{canCancel && <Button onClick={markAsCancelled} bsStyle='danger' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faTimes} /> {t('PaymentHistory_MarkAsCancelled')}</Button>}
					{canSendInvoice && <ConfirmationButton
						onConfirmation={sendInvoice}
						confirmationTitle={t('PaymentHistory_SendInvoiceTitle')}
						confirmationMessage={t('PaymentHistory_SendInvoiceConfirmationMessage')}
						displayType={'inline'}
						bsStyle='warning'
						icon={faEnvelope}
						btnClasses='ml-05'
						label={t('PaymentHistory_SendInvoice')} />
					}
					{canApplyPromoCode && <Button onClick={onApplyPromoCode} bsStyle='warning' style={{ marginRight: '5px' }}><FontAwesomeIcon icon={faDollarSign} /> {t('PaymentHistory_PromoCode_Add')}</Button>}
					{canCancelSession && hasNoCanceledSessions && <ConfirmationButton bsStyle='warning' displayType='inline-block' style={{ marginLeft: '5px', marginBottom: '5px' }}
						onConfirmation={cancelAllSessions}
						confirmationTitle={t('WorkshopPayment_CancelAllSessionsConfirmationTitle')}
						confirmationMessage={t('WorkshopPayment_CancelAllSessionsConfirmationMessage')}
						label={t('WorkshopPayment_CancelLAllSessions')}
					/>}
					{canCancelSession && allowRefund && sessions && sessions.length > 0 && <Button bsStyle='danger' className='pull-right' style={{ marginLeft: '5px' }} onClick={cancelAndRefundAllSessions}><FontAwesomeIcon icon={faDollarSign} /> {t('WorkshopPayment_CancelAndRefundAllSessions')}</Button>}
				</div>
			</FormActions>
		</Form>
	);
};

export default reduxForm({ form: 'workshopPayment', enableReinitialize: true })(withTranslation()(SettingsForm));
